'use client';

import { useControllableState } from '@radix-ui/react-use-controllable-state';
import { useCallback, useEffect, useRef, useState } from 'react';

const DEFAULT_AUTO_CLOSE_DELAY = 1000;

interface UseAutoCloseCollapsibleOptions {
  isStreaming: boolean;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  autoCloseDelay?: number;
}

interface UseAutoCloseCollapsibleResult {
  isOpen: boolean;
  handleOpenChange: (open: boolean) => void;
}

export function useAutoCloseCollapsible({
  isStreaming,
  open,
  defaultOpen = false,
  onOpenChange,
  autoCloseDelay = DEFAULT_AUTO_CLOSE_DELAY,
}: UseAutoCloseCollapsibleOptions): UseAutoCloseCollapsibleResult {
  const [isOpen, setIsOpen] = useControllableState({
    prop: open,
    defaultProp: defaultOpen,
    onChange: onOpenChange,
  });
  const [hasAutoClosed, setHasAutoClosed] = useState(false);
  const [wasEverStreaming, setWasEverStreaming] = useState(false);
  const userInteractedRef = useRef(false);

  useEffect(() => {
    if (isStreaming) {
      setWasEverStreaming(true);
    }
  }, [isStreaming]);

  // Auto-close when streaming ends (once only).
  // Only auto-close if streaming was active at some point — never auto-close
  // a manually opened toggle (e.g. for backend-loaded messages).
  useEffect(() => {
    if (!isStreaming && isOpen && !defaultOpen && !hasAutoClosed && !userInteractedRef.current && wasEverStreaming) {
      const timer = setTimeout(() => {
        setIsOpen(false);
        setHasAutoClosed(true);
      }, autoCloseDelay);
      return () => clearTimeout(timer);
    }
  }, [isStreaming, isOpen, defaultOpen, setIsOpen, hasAutoClosed, wasEverStreaming, autoCloseDelay]);

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      userInteractedRef.current = true;
      setIsOpen(newOpen);
    },
    [setIsOpen],
  );

  return { isOpen: isOpen ?? false, handleOpenChange };
}
